interface ErrorReportPayload {
  type: "react_error" | "unhandled_rejection" | "console_error";
  message: string;
  stack?: string;
  component_stack?: string;
  url: string;
  user_agent: string;
  user_id?: number;
  timestamp: string;
  extra?: Record<string, unknown>;
}

const isProduction = import.meta.env.PROD;
const MAX_QUEUE_SIZE = 20;
const queue: ErrorReportPayload[] = [];
let flushing = false;

const flush = async (): Promise<void> => {
  if (flushing || queue.length === 0) return;
  flushing = true;
  queue.splice(0, queue.length);
  flushing = false;
};

const enqueue = (payload: ErrorReportPayload): void => {
  if (queue.length >= MAX_QUEUE_SIZE) {
    queue.shift();
  }
  queue.push(payload);
  if (!isProduction) {
    // En dev solo loguear a consola, no enviar
    console.warn("[errorReporter]", payload);
    return;
  }
  void flush();
};

const getUserId = (): number | undefined => {
  try {
    const cached = localStorage.getItem("user_id");
    return cached ? Number(cached) : undefined;
  } catch {
    return undefined;
  }
};

export const reportError = (
  type: ErrorReportPayload["type"],
  error: Error | string,
  extra?: { componentStack?: string; [key: string]: unknown },
): void => {
  const message = typeof error === "string" ? error : error.message;
  const stack = typeof error === "string" ? undefined : error.stack;
  enqueue({
    type,
    message,
    stack,
    component_stack: extra?.componentStack,
    url: typeof window !== "undefined" ? window.location.href : "",
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    user_id: getUserId(),
    timestamp: new Date().toISOString(),
    extra,
  });
};

export const initErrorReporter = (): void => {
  if (typeof window === "undefined") return;

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    reportError("unhandled_rejection", reason);
  });

  const originalError = console.error;
  console.error = (...args: unknown[]) => {
    originalError.apply(console, args);
    const message = args
      .map((a) => (a instanceof Error ? a.message : String(a)))
      .join(" ");
    const stack = args.find((a) => a instanceof Error)?.stack as string | undefined;
    reportError("console_error", stack ? new Error(message) : message, { stack });
  };
};